/**
 * WhatsApp Messages Service
 *
 * Data access layer for whatsapp_messages table.
 * Handles WhatsApp message template operations.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import type { Database } from '../lib/database.types';

export type WhatsappMessage = Database['public']['Tables']['whatsapp_messages']['Row'];
export type WhatsappMessageInsert = Database['public']['Tables']['whatsapp_messages']['Insert'];
export type WhatsappMessageUpdate = Database['public']['Tables']['whatsapp_messages']['Update'];

/**
 * Get all WhatsApp messages for a location
 */
export async function getWhatsappMessages(locationId: string, language?: string) {
  try {
    let query = supabase
      .from('whatsapp_messages')
      .select('*')
      .eq('location_id', locationId)
      .order('category', { ascending: true })
      .order('reference', { ascending: true })
      .order('offset_minutes', { ascending: true });

    if (language) {
      query = query.eq('language', language);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get a single WhatsApp message by id
 */
export async function getWhatsappMessageById(id: string) {
  try {
    const { data, error } = await supabase
      .from('whatsapp_messages')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new WhatsApp message
 */
export async function createWhatsappMessage(data: WhatsappMessageInsert) {
  try {
    const { data: newMessage, error } = await supabase
      .from('whatsapp_messages')
      .insert(data)
      .select()
      .single();

    if (error) throw error;

    return { data: newMessage, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update a WhatsApp message
 */
export async function updateWhatsappMessage(id: string, data: WhatsappMessageUpdate) {
  try {
    const { data: updatedMessage, error } = await supabase
      .from('whatsapp_messages')
      .update({
        ...data,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data: updatedMessage, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete a WhatsApp message
 */
export async function deleteWhatsappMessage(id: string) {
  try {
    const { error } = await supabase
      .from('whatsapp_messages')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}
